<template>
  <div
    class="sb-minibrowser__input-container"
    :class="{
      'sb-minibrowser__input-container--loading': isLoading,
    }"
  >
    <SbTextField
      ref="input"
      :icon-left="icon.name"
      :icon-color="icon.color"
      type="search"
      :model-value="modelValue"
      :data-testid="`${dataTestid}-input`"
      :placeholder="placeholder"
      :clearable="showCloseIcon"
      @input="handleSearchInput"
      @keydown="handleSearchKeydown"
      @clear="clearSearchInputValue"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, useAttrs, watch } from 'vue'
import SbTextField from '../../TextField'
import { useMinibrowser } from '../utils'
import type { IconColor } from '../../Icon/types'

type SbMinibrowserSearchProps = {
  /**
   * Placeholder text for the search input
   */
  placeholder?: string

  /**
   * The value of the search input
   */
  modelValue: string
}

const props = withDefaults(defineProps<SbMinibrowserSearchProps>(), {
  placeholder: '',
  modelValue: '',
})

const emit = defineEmits(['update:modelValue', 'keydown'])

const { context } = useMinibrowser()
const input = ref<typeof SbTextField | null>(null)

const dataTestid = computed(
  () => useAttrs()['data-testid'] || 'sb-mini-browser-search',
)

const icon = computed<{
  name: string
  color?: IconColor
}>(() => ({
  name: isLoading.value ? 'loading' : 'search',
  color: isLoading.value ? 'primary' : undefined,
}))

const isLoading = computed(() => context.value.isLoading)

const hasValue = computed(
  () => typeof props.modelValue === 'string' && props.modelValue.length > 0,
)

const showCloseIcon = computed(() => hasValue.value && !isLoading.value)

const shouldFocusOnInput = computed(() => context.value.shouldFocusOnInput)

watch(
  shouldFocusOnInput,
  (value) => {
    if (value) {
      input.value?.focus()
      context.value.shouldFocusOnInput.value = false
    }
  },
  { deep: true },
)

const clearSearchInputValue = () => {
  emit('update:modelValue', '')
}

const handleSearchInput = (event: { target: HTMLInputElement }) => {
  emit('update:modelValue', event.target?.value ?? '')
}

const handleSearchKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    if (props.modelValue.length) {
      clearSearchInputValue()
      return
    }
  }

  if (event.key === 'ArrowDown') {
    context.value.focusOnFirstItem()
    event.preventDefault()
    event.stopPropagation()
    return
  }

  if (event.key === 'ArrowUp') {
    context.value.focusOnLastItem()
    event.preventDefault()
    event.stopPropagation()
    return
  }

  emit('keydown', event)
}
</script>
